import * as React from 'react';
import {FunctionComponent} from 'react';
import {observer} from 'mobx-react-lite';
import {PresenterProps, withPresenter} from '../../../../../Support/with_presenter';
import {EditScheduledEventPresenter, EditScheduledEventScreen} from './EditScheduledEventPresenter';
import {DatePicker} from './Components/DatePicker';
import {Duration} from '../../../../../Domain/Duration';
import {Crew} from '../../../../../Models/Crew';
import {CalendarEvent} from '../../../../../Models/CalendarEvent';
import {DeleteRecurring} from './Components/DeleteRecurring';
import {EditRecurring} from './Components/EditRecurring';
import Modal from '../../../../Components/Modal';

interface OwnProps {
    crew: Crew;
    calendarEvent: CalendarEvent;
    updateDateTo?: Date;
    onFinish: () => void;
}

const EditScheduledEventComponent: FunctionComponent<OwnProps & PresenterProps<EditScheduledEventPresenter>> = observer(
    ({onFinish, presenter}) => {
        if (presenter.screen?.screen === EditScheduledEventScreen.EditRecurring) {
            return (
                <Modal visible onHide={() => onFinish()}>
                    <EditRecurring
                        onCancel={() => onFinish()}
                        allowedUpdateTypes={presenter.screen.allowedUpdateTypes}
                        onSubmit={(type) => {
                            presenter.onEditRecurring(type);
                        }}
                    />
                </Modal>
            );
        }
        if (presenter.screen?.screen === EditScheduledEventScreen.DeleteRecurring) {
            return (
                <Modal visible wide onHide={() => onFinish()}>
                    <DeleteRecurring
                        onCancel={() => onFinish()}
                        onSubmit={(type) => {
                            presenter.onDeleteRecurring(type);
                        }}
                    />
                </Modal>
            );
        }
        return (
            <Modal visible={presenter.screen !== null} wide onHide={() => onFinish()}>
                <div className="modal-header">
                    <h5 className="modal-title">Edit event</h5>
                </div>
                <div className="modal-body">
                    <form
                        method="POST"
                        onSubmit={(e) => {
                            e.preventDefault();
                            presenter.onSubmit();
                            return false;
                        }}
                        className="form-horizontal"
                    >
                        <div className="mb-3">
                            <label htmlFor="title" className="form-label mb-2">
                                Title
                            </label>
                            <input
                                className="form-control"
                                placeholder="Title"
                                autoFocus
                                name="title"
                                type="text"
                                id="title"
                                value={presenter.title}
                                onChange={(e) => presenter.setTitle(e.target.value)}
                            />
                        </div>
                        <div className="row">
                            <div className="col-12 col-md-6">
                                <div className="mb-3">
                                    <div className="row">
                                        <div className="col-6">
                                            <label htmlFor="date" className="form-label mb-2">
                                                Date
                                            </label>
                                            <DatePicker
                                                date={presenter.startAt}
                                                onChange={(date) => presenter.setStartAt(date)}
                                            />
                                        </div>
                                        <div className="col-6">
                                            <label htmlFor="time" className="form-label mb-2">
                                                Time
                                            </label>
                                            <select
                                                className="form-select"
                                                name="time"
                                                id="time"
                                                value={presenter.time}
                                                onChange={(e) => presenter.setTime(e.target.value)}
                                            >
                                                <option value="">Select a time</option>
                                                {presenter.timeOptions.map((time) => (
                                                    <option key={time} value={time}>
                                                        {time}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="col-12 col-md-6">
                                <div className="mb-3">
                                    <label htmlFor="time" className="form-label mb-2">
                                        Duration (min.)
                                    </label>
                                    <input
                                        className="form-control"
                                        placeholder="Duration"
                                        name="duration"
                                        type="text"
                                        id="duration"
                                        value={presenter.duration?.minutes}
                                        onChange={(e) => {
                                            if (e.target.value === '') {
                                                presenter.setDuration(null);
                                            } else {
                                                presenter.setDuration(
                                                    Duration.fromMinutes(parseInt(e.target.value, 10)),
                                                );
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="mb-3">
                            <label htmlFor="description" className="form-label mb-2">
                                Description
                            </label>
                            <input
                                className="form-control"
                                placeholder="Description"
                                name="description"
                                type="text"
                                id="description"
                                value={presenter.description}
                                onChange={(e) => presenter.setDescription(e.target.value)}
                            />
                        </div>
                    </form>
                </div>
                <div className="modal-footer justify-content-between">
                    <button
                        onClick={() => {
                            return presenter.onDelete();
                        }}
                        className="btn btn-danger"
                    >
                        Delete
                    </button>

                    <div>
                        <button onClick={() => onFinish()} className="btn btn-transparent me-2">
                            Cancel
                        </button>
                        <button
                            className="btn btn-primary"
                            onClick={() => presenter.onSubmit()}
                            disabled={!presenter.validates}
                        >
                            Save
                        </button>
                    </div>
                </div>
            </Modal>
        );
    },
);

export const EditScheduledEvent = withPresenter<EditScheduledEventPresenter, OwnProps>(
    (props, component) =>
        new EditScheduledEventPresenter(
            props.updateDateTo,
            props.crew,
            props.calendarEvent,
            props.onFinish,
            component.events,
        ),
    EditScheduledEventComponent,
);
